import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import NewsLayout from "../../components/news/NewsLayout";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Search } from "lucide-react";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";
import Seo from "../../components/news/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const NewsSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(query);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { publicProject, publicProjectLoading } = useProject();

  useEffect(() => {
    const search = async () => {
      if (!query || publicProjectLoading) { setArticles([]); return; }
      setLoading(true);
      try {
        if (publicProject) {
          const res = await axios.get(`${API}/public/articles?project_id=${publicProject.id}&search=${encodeURIComponent(query)}&limit=30`);
          setArticles(res.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    search();
  }, [query, publicProject, publicProjectLoading]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) setSearchParams({ q: searchInput.trim() });
  };

  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || "News";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const isEspresso = theme === "espresso";
  const isRio = theme === "rio";

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <Seo
        title={query ? `Search results for "${query}"` : "Search"}
        description={query
          ? `Search results for "${query}" on ${siteName} — the latest matching stories, updated live.`
          : `Search ${siteName} for the latest news, analysis, and breaking stories.`}
        siteName={siteName}
        canonical={pageUrl}
        robots={query ? "noindex, follow" : "index, follow"}
      />
      <div className={`max-w-4xl mx-auto px-4 lg:px-8 py-12`} data-testid="news-search-page">
        <h1
          className={`mb-6 ${isEspresso ? "font-display text-5xl font-bold" : isRio ? "font-display uppercase text-5xl" : "text-3xl font-bold"}`}
          style={
            isEspresso ? { color: "var(--em-text)" }
            : isRio ? { color: "var(--rio-text)", fontFamily: "'Anton', sans-serif" }
            : undefined
          }
        >
          {isRio ? "// SEARCH" : "Search"}
        </h1>

        <form onSubmit={handleSearch} className="flex gap-2 mb-10">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder={isRio ? "QUERY..." : "Search for news..."}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`pl-10 h-12 ${isRio ? "rounded-none uppercase" : ""}`}
              style={
                isEspresso ? { border: "1px solid var(--em-border)", backgroundColor: "transparent", color: "var(--em-text)" }
                : isRio ? { border: "1px solid var(--rio-border)", backgroundColor: "var(--rio-surface)", color: "var(--rio-text)", fontFamily: "'JetBrains Mono', monospace" }
                : undefined
              }
              data-testid="search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: isEspresso ? "var(--em-text-muted)" : isRio ? "var(--rio-text-muted)" : "#64748B" }} />
          </div>
          <Button
            type="submit"
            className={`h-12 px-6 ${isRio ? "rounded-none uppercase" : isEspresso ? "rounded-full" : ""}`}
            style={
              isEspresso ? { backgroundColor: "var(--em-accent)", color: "#FDFBF7" }
              : isRio ? { backgroundColor: "var(--rio-accent)", color: "#000", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", fontWeight: 700 }
              : undefined
            }
            data-testid="search-submit"
          >
            {isRio ? "GO" : "Search"}
          </Button>
        </form>

        {query && (
          <p className="mb-8" style={{ color: isEspresso ? "var(--em-text-muted)" : isRio ? "var(--rio-text-muted)" : "#64748B" }}>
            {loading ? (isRio ? "// QUERYING..." : "Searching...") : `${articles.length} ${articles.length === 1 ? "result" : "results"} for "${query}"`}
          </p>
        )}

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 w-full" style={isRio ? { borderRadius: 0 } : undefined} />)}
          </div>
        ) : articles.length > 0 ? (
          <div className="space-y-6">
            {articles.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.slug}`}
                className="block group transition-all"
                data-testid={`search-result-${a.id}`}
              >
                <div
                  className={`flex gap-5 p-5 ${isRio ? "rio-card" : ""}`}
                  style={
                    isEspresso ? { borderBottom: "1px solid var(--em-border)" }
                    : isRio ? undefined
                    : { borderBottom: "1px solid #E2E8F0" }
                  }
                >
                  {a.featured_image && (
                    <div className={`shrink-0 w-32 h-24 overflow-hidden ${isEspresso ? "em-image-wrap" : ""}`} style={isRio ? { border: "1px solid var(--rio-border)" } : undefined}>
                      <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase mb-1" style={{ color: isEspresso ? "var(--em-accent)" : isRio ? "var(--rio-accent)" : "#D97706", fontFamily: isRio ? "'JetBrains Mono', monospace" : undefined, letterSpacing: isRio || isEspresso ? "0.15em" : undefined }}>
                      {a.category}
                    </p>
                    <h3
                      className={`mb-1 group-hover:opacity-80 transition ${isEspresso ? "font-display text-xl font-bold" : isRio ? "font-display uppercase text-xl" : "font-bold text-lg"}`}
                      style={isEspresso ? { color: "var(--em-text)" } : isRio ? { color: "var(--rio-text)", fontFamily: "'Anton', sans-serif" } : undefined}
                    >
                      {a.title}
                    </h3>
                    {a.excerpt && <p className="text-sm line-clamp-2" style={{ color: isEspresso ? "var(--em-text-muted)" : isRio ? "var(--rio-text-muted)" : "#64748B" }}>{a.excerpt}</p>}
                    <p className="text-xs mt-2" style={{ color: isEspresso ? "var(--em-text-muted)" : isRio ? "var(--rio-text-muted)" : "#94A3B8" }}>{formatDate(a.created_at)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : query ? (
          <p style={{ color: isEspresso ? "var(--em-text-muted)" : isRio ? "var(--rio-text-muted)" : "#64748B" }}>
            {isRio ? "// NO MATCHES" : "No results found."}
          </p>
        ) : null}
      </div>
    </NewsLayout>
  );
};

export default NewsSearch;
